import { Extension, ReactRenderer, type Editor, type Range } from '@tiptap/react';
import Suggestion, { type SuggestionOptions, type SuggestionProps } from '@tiptap/suggestion';
import tippy, { type Instance } from 'tippy.js';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { insertCallout } from './callout-extension';
import { requestFkTableImport } from './fk-table';
import { mediaEmbedAttrs } from './media-embed';
import { LAYOUT_TEMPLATES } from './layout-nodes';
import { MISC_TEMPLATES } from './misc-nodes';
import { diskFileUrl, type PickedDiskFile } from '../lib/api/kb';

/**
 * Slash-меню в стиле Teamly/Notion. Срабатывает на «/» в начале строки.
 *
 * Пункты — заголовки, списки, чек-листы, таблицы, цитата, код, разделитель.
 * Каждый пункт имеет иконку, название, описание и команду которая
 * вставит блок и удалит «/» query.
 */
interface SlashItem {
  title: string;
  description: string;
  icon: string; // emoji или иконка-символ
  keywords: string[]; // дополнительные ключевые слова для поиска
  command: (props: { editor: Editor; range: Range }) => void;
}

/** Загрузчик файла из редактора (картинка/файл) → отдаёт URL для вставки. */
export type SlashUploadFn = (
  file: File,
) => Promise<{ url: string; fileName: string; kind: string; id?: string } | null>;

/** Окно «Файл с Диска»: промис резолвится выбранными файлами (пусто — закрыли без выбора). */
export type SlashPickDiskFilesFn = () => Promise<PickedDiskFile[]>;

export interface SlashCommandsOptions {
  suggestion: Partial<SuggestionOptions>;
  /** Если задан — «Изображение» и «Файл» грузят файл с компьютера; иначе спрашиваем URL. */
  uploadFile?: SlashUploadFn;
  /** Если задан — в меню есть пункт «Файл с Диска» (окно выбора файлов Диска вики). */
  pickDiskFiles?: SlashPickDiskFilesFn;
}

/** Открыть нативный диалог выбора файла. Возвращает File или null. */
function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';
    input.onchange = () => {
      resolve(input.files?.[0] ?? null);
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  });
}

/** Экранируем для безопасной вставки в HTML-атрибут/текст. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function buildSlashItems(opts: SlashCommandsOptions): SlashItem[] {
  return [
  {
    title: 'Заголовок 1',
    description: 'Крупный заголовок секции',
    icon: 'H1',
    keywords: ['h1', 'heading', 'заголовок', 'title'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run();
    },
  },
  {
    title: 'Заголовок 2',
    description: 'Подзаголовок',
    icon: 'H2',
    keywords: ['h2', 'heading'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run();
    },
  },
  {
    title: 'Заголовок 3',
    description: 'Малый заголовок',
    icon: 'H3',
    keywords: ['h3', 'heading'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run();
    },
  },
  {
    title: 'Маркированный список',
    description: 'Простой ненумерованный список',
    icon: '•',
    keywords: ['ul', 'list', 'bullet', 'список'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: 'Нумерованный список',
    description: 'Список с цифрами',
    icon: '1.',
    keywords: ['ol', 'numbered', 'нумерованный'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: 'Чек-лист',
    description: 'Список задач с галочками',
    icon: '☑',
    keywords: ['todo', 'task', 'checkbox', 'чек'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleTaskList().run();
    },
  },
  {
    title: 'Цитата',
    description: 'Выделенный блок-цитата',
    icon: '❝',
    keywords: ['quote', 'blockquote', 'цитата'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    title: 'Блок кода',
    description: 'Многострочный код',
    icon: '</>',
    keywords: ['code', 'pre', 'код'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
  // ── Выделенный блок (callout) — один пункт; тип выбирается внутри блока
  //    (всплывающее меню эмодзи при клике в блок — CalloutBubbleMenu). ──
  {
    title: 'Блок-выноска',
    description: 'Цветной блок — тип выберите внутри (клик по блоку)',
    icon: '🔔',
    keywords: ['callout', 'блок', 'выноска', 'примечание', 'внимание', 'совет', 'идея', 'готово', 'ошибка'],
    command: ({ editor, range }: { editor: Editor; range: Range }) => {
      insertCallout(editor, range, 'info');
    },
  },
  {
    title: 'Таблица',
    description: 'Поля, формулы, фильтр, сортировка, группировка, статусы',
    icon: '▦',
    keywords: ['table', 'таблица', 'grid', 'грид', 'база', 'данные', 'формула', 'статус'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent({ type: 'fkTable' }).run();
    },
  },
  {
    title: 'Таблица из Excel / CSV',
    description: 'Новая таблица и сразу импорт: файл .xlsx / .csv или вставка из Google Таблиц',
    icon: '📥',
    keywords: ['import', 'excel', 'csv', 'xlsx', 'sheets', 'импорт', 'эксель', 'эксел', 'гугл', 'таблица', 'загрузить', 'файл'],
    command: ({ editor, range }) => {
      // Одноразовый локальный флаг: NodeView новой таблицы откроет диалог импорта.
      // Не атрибут ноды — он ушёл бы через Yjs соавторам и в сохранённый текст.
      requestFkTableImport();
      editor.chain().focus().deleteRange(range).insertContent({ type: 'fkTable' }).run();
    },
  },
  {
    title: 'Разделитель',
    description: 'Горизонтальная линия',
    icon: '—',
    keywords: ['hr', 'divider', 'разделитель', 'line'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
  },
  {
    title: 'Изображение',
    description: 'Блок: загрузить файл, URL или из буфера',
    icon: '🖼',
    keywords: ['image', 'img', 'picture', 'картинка', 'фото'],
    command: ({ editor, range }) => {
      // Вставляем пустой блок-плейсхолдер — источник выбирается в самом блоке.
      editor.chain().focus().deleteRange(range).insertContent(mediaEmbedAttrs('image')).run();
    },
  },
  {
    title: 'Файл',
    description: opts.uploadFile ? 'Прикрепить файл (PDF, Word, Excel…)' : 'Вставить ссылку на файл',
    icon: '📎',
    keywords: ['file', 'attachment', 'файл', 'документ', 'pdf', 'вложение'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      const upload = opts.uploadFile;
      if (upload) {
        void (async () => {
          const file = await pickFile('.pdf,.doc,.docx,.xls,.xlsx,.zip,application/*');
          if (!file) return;
          const r = await upload(file).catch(() => null);
          if (r) {
            // Карточка файла (фаза 1.7) вместо строки-ссылки.
            editor.chain().focus().insertContent({ type: 'fkFile', attrs: { url: r.url, name: r.fileName, size: file.size } }).run();
          }
        })();
      } else {
        const url = window.prompt('URL файла:');
        if (url) {
          const name = window.prompt('Название файла:', 'Документ') || 'Документ';
          editor.chain().focus().insertContent(`<p>📎 <a href="${escapeHtml(url)}">${escapeHtml(name)}</a></p>`).run();
        }
      }
    },
  },
  // «Файл с Диска» (22.10.2026): готовый файл из Диска вики — карточкой, без копирования ссылок.
  ...(opts.pickDiskFiles
    ? [{
        title: 'Файл с Диска',
        description: 'Выбрать готовые файлы из Диска (поиск по названию)',
        icon: '🗂',
        keywords: ['диск', 'disk', 'drive', 'файл', 'file', 'документ', 'pdf', 'вложение', 'стандарт'],
        command: ({ editor, range }: { editor: Editor; range: Range }) => {
          editor.chain().focus().deleteRange(range).run();
          const pick = opts.pickDiskFiles;
          if (!pick) return;
          void (async () => {
            const files = await pick().catch(() => [] as PickedDiskFile[]);
            if (files.length === 0) return;
            // Карточки файла (fkFile) — тот же блок, что у «Файл»; адрес — скачивание файла Диска.
            editor
              .chain()
              .focus()
              .insertContent(files.map((f) => ({ type: 'fkFile', attrs: { url: diskFileUrl(f.id), name: f.fileName, size: f.size } })))
              .run();
          })();
        },
      } satisfies SlashItem]
    : []),
  {
    title: 'Видео',
    description: 'Блок: загрузить файл, URL или из буфера',
    icon: '🎬',
    keywords: ['video', 'видео', 'youtube', 'rutube', 'vk'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent(mediaEmbedAttrs('video')).run();
    },
  },
  {
    title: 'Колонки',
    description: 'Текст в 2–4 колонки',
    icon: '▥',
    keywords: ['columns', 'колонки', 'столбцы', 'layout'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent(LAYOUT_TEMPLATES.columns).run();
    },
  },
  {
    title: 'Раскрывающийся блок',
    description: 'Заголовок, по клику раскрывается содержимое',
    icon: '▸',
    keywords: ['toggle', 'details', 'аккордеон', 'раскрыть', 'спойлер', 'faq'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent(LAYOUT_TEMPLATES.toggle).run();
    },
  },
  {
    title: 'Шаги',
    description: 'Пошаговая инструкция с крупной нумерацией',
    icon: '①',
    keywords: ['steps', 'шаги', 'инструкция', 'пошагово', 'процедура'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent(LAYOUT_TEMPLATES.steps).run();
    },
  },
  {
    title: 'Встроить (видео/документ/доска)',
    description: 'YouTube, RuTube, VK, Google Docs/Sheets, Figma, Miro, Яндекс.Карты',
    icon: '🔗',
    keywords: ['embed', 'встроить', 'iframe', 'youtube', 'rutube', 'figma', 'miro', 'sheets', 'таблица google', 'карта'],
    command: ({ editor, range }) => {
      const url = window.prompt('Ссылка для встраивания:');
      editor.chain().focus().deleteRange(range).run();
      if (url && url.trim()) editor.chain().focus().insertContent({ type: 'fkEmbed', attrs: { url: url.trim(), height: 480 } }).run();
    },
  },
  {
    title: 'Вкладки',
    description: 'Несколько вкладок, читатель переключает',
    icon: '⧉',
    keywords: ['tabs', 'вкладки', 'табы'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent(MISC_TEMPLATES.tabs).run();
    },
  },
  {
    title: 'Кнопка',
    description: 'Заметная кнопка-ссылка (CTA)',
    icon: '🔘',
    keywords: ['button', 'кнопка', 'cta', 'ссылка'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent({ type: 'fkButton', attrs: { label: 'Открыть', href: '', style: 'primary' } }).run();
    },
  },
  {
    title: 'KPI-виджет',
    description: 'Показатель с дашборда по коду KPI',
    icon: '📈',
    keywords: ['kpi', 'показатель', 'метрика', 'дашборд'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent({ type: 'fkKpi', attrs: { code: '' } }).run();
    },
  },
  {
    title: 'Проверь себя',
    description: 'Вопрос с вариантами внутри текста (самопроверка, не зачёт)',
    icon: '✅',
    keywords: ['check', 'проверь', 'вопрос', 'квиз', 'самопроверка', 'тест'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertContent({ type: 'fkCheck', attrs: { q: '', options: ['', ''], correct: 0, explanation: '' } }).run();
    },
  },
  {
    title: 'Галерея',
    description: 'Несколько картинок в ряд (2–5 колонок), как карусель',
    icon: '▦',
    keywords: ['gallery', 'галерея', 'карусель', 'ряд', 'коллаж', 'images', 'фото'],
    command: ({ editor, range }) => {
      // Пустой блок-галерея — картинки добавляются внутри блока.
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({ type: 'imageGallery', attrs: { images: [], cols: 3 } })
        .run();
    },
  },
  ];
}

// React-компонент списка-меню
interface SlashListProps {
  items: SlashItem[];
  command: (item: SlashItem) => void;
}

interface SlashListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

const SlashList = forwardRef<SlashListHandle, SlashListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [items]);

  const select = (idx: number) => {
    const it = items[idx];
    if (it) command(it);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((selectedIndex + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((selectedIndex + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        select(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="slash-menu">
        <div className="slash-menu-empty">Ничего не найдено</div>
      </div>
    );
  }

  return (
    <div className="slash-menu">
      {items.map((it, idx) => (
        <button
          key={it.title}
          type="button"
          className={`slash-menu-item ${idx === selectedIndex ? 'is-selected' : ''}`}
          onClick={() => select(idx)}
          onMouseEnter={() => setSelectedIndex(idx)}
        >
          <span className="slash-menu-icon">{it.icon}</span>
          <span className="slash-menu-text">
            <span className="slash-menu-title">{it.title}</span>
            <span className="slash-menu-desc">{it.description}</span>
          </span>
        </button>
      ))}
    </div>
  );
});
SlashList.displayName = 'SlashList';

export const SlashCommands = Extension.create({
  name: 'slashCommands',

  addOptions(): SlashCommandsOptions {
    return {
      suggestion: {
        char: '/',
        startOfLine: false,
        command: ({ editor, range, props }) => {
          (props as SlashItem).command({ editor, range });
        },
      },
      uploadFile: undefined,
      pickDiskFiles: undefined,
    };
  },

  addProseMirrorPlugins() {
    const options = this.options;
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        items: ({ query }: { query: string }) => {
          const items = buildSlashItems(options);
          const q = query.toLowerCase();
          if (!q) return items;
          return items.filter(
            (it) =>
              it.title.toLowerCase().includes(q) ||
              it.keywords.some((k) => k.toLowerCase().includes(q)),
          );
        },
        render: () => {
          let component: ReactRenderer<SlashListHandle, SlashListProps> | null = null;
          let popup: Instance[] | null = null;
          return {
            onStart: (props: SuggestionProps) => {
              component = new ReactRenderer(SlashList, {
                props: {
                  items: props.items as SlashItem[],
                  command: (item: SlashItem) => {
                    props.command(item as unknown as Record<string, unknown>);
                  },
                },
                editor: props.editor,
              });
              if (!props.clientRect) return;
              popup = tippy('body', {
                getReferenceClientRect: props.clientRect as () => DOMRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                animation: false,
              });
            },
            onUpdate: (props: SuggestionProps) => {
              component?.updateProps({
                items: props.items as SlashItem[],
                command: (item: SlashItem) => {
                  props.command(item as unknown as Record<string, unknown>);
                },
              });
              if (popup?.[0] && props.clientRect) {
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect as () => DOMRect,
                });
              }
            },
            onKeyDown: (props: { event: KeyboardEvent }) => {
              if (props.event.key === 'Escape') {
                popup?.[0]?.hide();
                return true;
              }
              return component?.ref?.onKeyDown(props.event) ?? false;
            },
            onExit: () => {
              popup?.[0]?.destroy();
              component?.destroy();
            },
          };
        },
      }),
    ];
  },
});
